import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, normalizeClass as _normalizeClass, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "install-dialog" }
const _hoisted_2 = { class: "install-dialog__title" }
const _hoisted_3 = {
  key: 0,
  class: "install-dialog__errors"
}
const _hoisted_4 = { class: "mb-20" }
const _hoisted_5 = { class: "mb-20" }
const _hoisted_6 = { class: "mb-20" }
const _hoisted_7 = { class: "mb-10" }
const _hoisted_8 = { class: "install-dialog__checkbox-label" }
const _hoisted_9 = ["disabled"]
const _hoisted_10 = { class: "mb-10 install-dialog__indent" }
const _hoisted_11 = { class: "mb-20 install-dialog__indent" }
const _hoisted_12 = { class: "mb-10" }
const _hoisted_13 = { class: "install-dialog__checkbox-label" }
const _hoisted_14 = ["disabled"]
const _hoisted_15 = { class: "mb-10 install-dialog__indent" }
const _hoisted_16 = { class: "mb-20 install-dialog__indent" }
const _hoisted_17 = { class: "install-dialog__actions" }
const _hoisted_18 = ["disabled"]
const _hoisted_19 = ["disabled"]
const _hoisted_20 = { class: "install-dialog__steps" }
const _hoisted_21 = { class: "install-dialog__step-icon" }
const _hoisted_22 = {
  key: 0,
  class: "icon icon-spinner icon-spin"
}
const _hoisted_23 = {
  key: 1,
  class: "icon icon-checkmark"
}
const _hoisted_24 = {
  key: 2,
  class: "icon icon-minus"
}
const _hoisted_25 = {
  key: 3,
  class: "icon icon-dot-open"
}
const _hoisted_26 = {
  key: 0,
  class: "install-dialog__step-detail"
}
const _hoisted_27 = { class: "install-dialog__status" }

import { ref, onUnmounted } from 'vue';
import { useStore } from 'vuex';

import { LabeledInput } from '@components/Form/LabeledInput';
import { Banner } from '@components/Banner';
import EpinioCluster from '../models/epiniomgmt/epinio.io.management.cluster';

const OPERATOR_NAMESPACE  = 'epinio-system';
const OPERATOR_IMAGE      = 'ghcr.io/epinio/install-operator:v1.0.1';
const OPERATOR_DEPLOYMENT = 'install-operator-controller-manager';
const OPERATOR_SA         = 'controller-manager';
const CR_GROUP            = 'epinio.apps.epinio.io';
const CR_VERSION          = 'v1alpha1';
const CR_PLURAL           = 'installepinios';

// form state
type Phase = 'form' | 'bootstrapping' | 'polling' | 'done' | 'failed';
type StepStatus = 'pending' | 'running' | 'done' | 'skipped';
interface BootstrapStep { label: string; status: StepStatus }
const POLL_MAX = 90; // 90 × 4 s = 6 min max wait for operator


export default /*@__PURE__*/_defineComponent({
  __name: 'InstallDialog',
  props: {
    cluster: {},
    onSuccess: { type: Function }
  },
  emits: ["close"],
  setup(__props: any, { emit: __emit }) {

const store = useStore();

const emit = __emit;

const props = __props;

// constants
const domain                     = ref('');
const targetNamespace            = ref('epinio');
const version                    = ref('');
const useExistingIngress         = ref(false);
const nginxReleaseName           = ref('');
const nginxReleaseNamespace      = ref('');
const useExistingCertManager     = ref(false);
const certManagerReleaseName     = ref('');
const certManagerReleaseNamespace = ref('');

// page state
const phase         = ref<Phase>('form');
const busy          = ref(false);
const errors        = ref<string[]>([]);
const bootstrapStep = ref('');

// steps list for bootstrapping UI
const bootstrapSteps = ref<BootstrapStep[]>([]);

// installed CR identity (for polling)
const crName        = ref('');
const statusMessage = ref('');

// polling
let pollTimer: ReturnType<typeof setTimeout> | null = null;
let pollCount = 0;
onUnmounted(() => stopPolling());

function stopPolling() {
  if (pollTimer !== null) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
}

// helpers
const cid = () => props.cluster.id;

function rawUrl(path: string) {
  return `/k8s/clusters/${ cid() }${ path }`;
}

function crUrl(name = '') {
  const base = rawUrl(`/apis/${ CR_GROUP }/${ CR_VERSION }/namespaces/${ OPERATOR_NAMESPACE }/${ CR_PLURAL }`);

  return name ? `${ base }/${ name }` : base;
}

async function k8sRequest(opt: Record<string, any>) {
  return store.dispatch('cluster/request', opt, { root: true });
}

function extractErrorMessage(err: any): string {
  if (typeof err === 'string') return err;
  if (err?._status) {
    const body = err?.message || err?.reason || err?._statusText;

    if (body) return `${ err._status }: ${ body }`;

    return `Request failed with status ${ err._status }`;
  }
  if (err?.response?.data?.message) return err.response.data.message;
  if (err?.message) return err.message;
  try {
    return JSON.stringify(err);
  } catch {
    return 'An unknown error occurred';
  }
}

async function applyResource(url: string, data: any): Promise<void> {
  try {
    await k8sRequest({ url, method: 'post', headers: { 'content-type': 'application/json' }, data });
  } catch (err: any) {
    const status = err?._status || err?.response?.status;

    if (status !== 409) throw err; // surface all non-conflict errors (including 422 validation)

    // 409 = resource already exists → patch it so stale resources get updated
    const name = data?.metadata?.name;

    if (!name) return;
    try {
      await k8sRequest({
        url:     `${ url }/${ name }`,
        method:  'patch',
        headers: { 'content-type': 'application/strategic-merge-patch+json' },
        data,
      });
    } catch {
      // patch failed - resource is there, close enough
    }
  }
}

function setStep(label: string) {
  bootstrapStep.value = label;
  const step = bootstrapSteps.value.find((s) => s.label === label);

  if (step) step.status = 'running';
}

function completeStep(label: string, skipped = false) {
  const step = bootstrapSteps.value.find((s) => s.label === label);

  if (step) step.status = skipped ? 'skipped' : 'done';
}

// operator bootstrap
async function isOperatorDeployed(): Promise<boolean> {
  try {
    const dep = await k8sRequest({ url: rawUrl(`/apis/apps/v1/namespaces/${ OPERATOR_NAMESPACE }/deployments/${ OPERATOR_DEPLOYMENT }`) });

    // Only consider it "deployed" if at least one replica is ready
    return (dep?.status?.readyReplicas ?? 0) >= 1;
  } catch {
    return false;
  }
}

async function bootstrapOperator(): Promise<void> {
  phase.value = 'bootstrapping';

  const steps = [
    'Check operator',
    'Create namespace',
    'Register CRD',
    'Create ServiceAccount',
    'Configure RBAC',
    'Deploy operator',
    'Wait for operator',
  ];

  bootstrapSteps.value = steps.map((label) => ({ label, status: 'pending' as StepStatus }));

  // 1 - check if already deployed
  setStep('Check operator');
  const deployed = await isOperatorDeployed();

  completeStep('Check operator');
  if (deployed) {
    for (const s of bootstrapSteps.value) {
      if (s.status === 'pending') s.status = 'skipped';
    }
    bootstrapStep.value = 'Operator already running — proceeding…';
    return;
  }

  // 2 - namespace
  setStep('Create namespace');
  await ensureOperatorNamespace();
  completeStep('Create namespace');

  // 3 - CRD
  setStep('Register CRD');
  await applyResource(
    rawUrl('/apis/apiextensions.k8s.io/v1/customresourcedefinitions'),
    {
      apiVersion: 'apiextensions.k8s.io/v1',
      kind:       'CustomResourceDefinition',
      metadata:   { name: 'installepinios.epinio.apps.epinio.io' },
      spec:       {
        group: CR_GROUP,
        names: {
          kind:     'InstallEpinio',
          listKind: 'InstallEpinioList',
          plural:   CR_PLURAL,
          singular: 'installepinio',
        },
        scope:    'Namespaced',
        versions: [{
          name:    CR_VERSION,
          served:  true,
          storage: true,
          subresources: { status: {} },
          schema:  {
            openAPIV3Schema: {
              type: 'object',
              required: ['spec'],
              properties: {
                apiVersion: { type: 'string' },
                kind:       { type: 'string' },
                metadata:   { type: 'object' },
                spec: {
                  type: 'object',
                  properties: {
                    domain:          { type: 'string' },
                    targetNamespace: { type: 'string' },
                    version:         { type: 'string' },
                    nginxReleaseName:            { type: 'string' },
                    nginxReleaseNamespace:        { type: 'string' },
                    certManagerReleaseName:      { type: 'string' },
                    certManagerReleaseNamespace: { type: 'string' },
                    imagePullSecret:          { type: 'string' },
                    imageRegistry:            { type: 'string' },
                    imageRegistryUsername:    { type: 'string' },
                    imageRegistryPassword:    { type: 'string' },
                  },
                },
                status: {
                  type: 'object',
                  properties: {
                    lastUpdateTime: { type: 'string', format: 'date-time' },
                    conditions: {
                      type:  'array',
                      'x-kubernetes-list-type':     'map',
                      'x-kubernetes-list-map-keys': ['type'],
                      items: {
                        type:     'object',
                        required: ['lastTransitionTime', 'message', 'reason', 'status', 'type'],
                        properties: {
                          lastTransitionTime: { type: 'string', format: 'date-time' },
                          message:            { type: 'string', maxLength: 32768 },
                          observedGeneration: { type: 'integer', minimum: 0 },
                          reason:             { type: 'string', minLength: 1, maxLength: 1024 },
                          status:             { type: 'string', enum: ['True', 'False', 'Unknown'] },
                          type:               { type: 'string', maxLength: 316 },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        }],
      },
    }
  );
  completeStep('Register CRD');

  // 4 - ServiceAccount
  setStep('Create ServiceAccount');
  await applyResource(
    rawUrl(`/api/v1/namespaces/${ OPERATOR_NAMESPACE }/serviceaccounts`),
    { apiVersion: 'v1', kind: 'ServiceAccount', metadata: { name: OPERATOR_SA, namespace: OPERATOR_NAMESPACE } }
  );
  completeStep('Create ServiceAccount');

  // 5 - RBAC
  setStep('Configure RBAC');
  // Manager ClusterRole
  await applyResource(
    rawUrl('/apis/rbac.authorization.k8s.io/v1/clusterroles'),
    {
      apiVersion: 'rbac.authorization.k8s.io/v1',
      kind:       'ClusterRole',
      metadata:   { name: 'install-operator-manager-role' },
      rules:      [
        { apiGroups: [''], resources: ['configmaps', 'namespaces'], verbs: ['get', 'list', 'watch', 'create', 'update', 'patch', 'delete'] },
        { apiGroups: ['batch'], resources: ['jobs'], verbs: ['get', 'list', 'watch', 'create', 'update', 'patch', 'delete'] },
        { apiGroups: [CR_GROUP], resources: [CR_PLURAL], verbs: ['get', 'list', 'watch', 'create', 'update', 'patch', 'delete'] },
        { apiGroups: [CR_GROUP], resources: [`${ CR_PLURAL }/finalizers`], verbs: ['update'] },
        { apiGroups: [CR_GROUP], resources: [`${ CR_PLURAL }/status`], verbs: ['get', 'patch', 'update'] },
      ],
    }
  );
  // Manager ClusterRoleBinding
  await applyResource(
    rawUrl('/apis/rbac.authorization.k8s.io/v1/clusterrolebindings'),
    {
      apiVersion: 'rbac.authorization.k8s.io/v1',
      kind:       'ClusterRoleBinding',
      metadata:   { name: 'install-operator-manager-rolebinding' },
      roleRef:    { apiGroup: 'rbac.authorization.k8s.io', kind: 'ClusterRole', name: 'install-operator-manager-role' },
      subjects:   [{ kind: 'ServiceAccount', name: OPERATOR_SA, namespace: OPERATOR_NAMESPACE }],
    }
  );
  // Installer ClusterRole (broad permissions so the Job can run Helm)
  // escalate + bind verbs are required to create ClusterRoles that grant
  // permissions the SA holds (e.g. Epinio's "reflector" ClusterRole).
  await applyResource(
    rawUrl('/apis/rbac.authorization.k8s.io/v1/clusterroles'),
    {
      apiVersion: 'rbac.authorization.k8s.io/v1',
      kind:       'ClusterRole',
      metadata:   { name: 'install-operator-installer-role' },
      rules:      [
        { apiGroups: ['*'], resources: ['*'], verbs: ['get', 'list', 'watch', 'create', 'update', 'patch', 'delete'] },
        {
          apiGroups: ['rbac.authorization.k8s.io'],
          resources: ['clusterroles', 'clusterrolebindings', 'roles', 'rolebindings'],
          verbs:     ['escalate', 'bind', 'get', 'list', 'watch', 'create', 'update', 'patch', 'delete'],
        },
      ],
    }
  );
  // Installer ClusterRoleBinding
  await applyResource(
    rawUrl('/apis/rbac.authorization.k8s.io/v1/clusterrolebindings'),
    {
      apiVersion: 'rbac.authorization.k8s.io/v1',
      kind:       'ClusterRoleBinding',
      metadata:   { name: 'install-operator-installer-rolebinding' },
      roleRef:    { apiGroup: 'rbac.authorization.k8s.io', kind: 'ClusterRole', name: 'install-operator-installer-role' },
      subjects:   [{ kind: 'ServiceAccount', name: OPERATOR_SA, namespace: OPERATOR_NAMESPACE }],
    }
  );
  // Leader-election Role + Binding
  await applyResource(
    rawUrl(`/apis/rbac.authorization.k8s.io/v1/namespaces/${ OPERATOR_NAMESPACE }/roles`),
    {
      apiVersion: 'rbac.authorization.k8s.io/v1',
      kind:       'Role',
      metadata:   { name: 'install-operator-leader-election-role', namespace: OPERATOR_NAMESPACE },
      rules:      [
        { apiGroups: [''], resources: ['configmaps'], verbs: ['get', 'list', 'watch', 'create', 'update', 'patch', 'delete'] },
        { apiGroups: ['coordination.k8s.io'], resources: ['leases'], verbs: ['get', 'list', 'watch', 'create', 'update', 'patch', 'delete'] },
        { apiGroups: [''], resources: ['events'], verbs: ['create', 'patch'] },
      ],
    }
  );
  await applyResource(
    rawUrl(`/apis/rbac.authorization.k8s.io/v1/namespaces/${ OPERATOR_NAMESPACE }/rolebindings`),
    {
      apiVersion: 'rbac.authorization.k8s.io/v1',
      kind:       'RoleBinding',
      metadata:   { name: 'install-operator-leader-election-rolebinding', namespace: OPERATOR_NAMESPACE },
      roleRef:    { apiGroup: 'rbac.authorization.k8s.io', kind: 'Role', name: 'install-operator-leader-election-role' },
      subjects:   [{ kind: 'ServiceAccount', name: OPERATOR_SA, namespace: OPERATOR_NAMESPACE }],
    }
  );
  completeStep('Configure RBAC');

  // 6 ── Deployment
  setStep('Deploy operator');
  await applyResource(
    rawUrl(`/apis/apps/v1/namespaces/${ OPERATOR_NAMESPACE }/deployments`),
    {
      apiVersion: 'apps/v1',
      kind:       'Deployment',
      metadata:   {
        name:      OPERATOR_DEPLOYMENT,
        namespace: OPERATOR_NAMESPACE,
        labels:    { 'control-plane': 'controller-manager', 'app.kubernetes.io/name': 'epinio' },
      },
      spec: {
        replicas: 1,
        selector: { matchLabels: { 'control-plane': 'controller-manager', 'app.kubernetes.io/name': 'epinio' } },
        template: {
          metadata: { labels: { 'control-plane': 'controller-manager', 'app.kubernetes.io/name': 'epinio' } },
          spec:     {
            serviceAccountName:            OPERATOR_SA,
            terminationGracePeriodSeconds: 10,
            containers: [{
              name:            'manager',
              image:           OPERATOR_IMAGE,
              imagePullPolicy: 'Always',
              command:         ['/manager'],
              args:            [
                '--health-probe-bind-address=:8081',
                '--ui-bind-address=:8082',
                '--installer-helm-image=alpine/helm:3.18',
              ],
              env: [
                { name: 'POD_NAMESPACE', valueFrom: { fieldRef: { fieldPath: 'metadata.namespace' } } },
                { name: 'POD_SERVICE_ACCOUNT', valueFrom: { fieldRef: { fieldPath: 'spec.serviceAccountName' } } },
              ],
              ports: [{ containerPort: 8082, name: 'ui', protocol: 'TCP' }],
              livenessProbe:  { httpGet: { path: '/healthz', port: 8081 }, initialDelaySeconds: 15, periodSeconds: 20 },
              readinessProbe: { httpGet: { path: '/readyz', port: 8081 }, initialDelaySeconds: 5, periodSeconds: 10 },
              resources: {
                limits:   { cpu: '500m', memory: '128Mi' },
                requests: { cpu: '10m', memory: '64Mi' },
              },
            }],
          },
        },
      },
    }
  );
  completeStep('Deploy operator');

  // 7 - wait for pod ready
  setStep('Wait for operator');
  await waitForDeploymentReady();
  completeStep('Wait for operator');
}

async function waitForDeploymentReady(): Promise<void> {
  const url = rawUrl(`/apis/apps/v1/namespaces/${ OPERATOR_NAMESPACE }/deployments/${ OPERATOR_DEPLOYMENT }`);
  const maxAttempts = 36; // 36 × 5 s = 3 min

  for (let i = 0; i < maxAttempts; i++) {
    await new Promise((resolve) => setTimeout(resolve, 5000));
    try {
      const dep = await k8sRequest({ url });
      const ready = dep?.status?.readyReplicas ?? 0;

      bootstrapStep.value = `Waiting for operator pod… (${ i + 1 }/${ maxAttempts })`;
      if (ready >= 1) return;
    } catch {
      // transient - keep waiting
    }
  }
  throw new Error('Operator did not become ready within 3 minutes. Check pod logs in epinio-system.');
}

async function waitForCRDReady(): Promise<void> {
  for (let i = 0; i < 10; i++) {
    await new Promise((resolve) => setTimeout(resolve, 2000));
    try {
      await k8sRequest({ url: crUrl() });

      return;
    } catch { /* keep waiting */ }
  }
}

// ensure namespace
async function ensureOperatorNamespace(): Promise<void> {
  try {
    await k8sRequest({ url: rawUrl(`/api/v1/namespaces/${ OPERATOR_NAMESPACE }`) });
  } catch (err: any) {
    const status = err?._status || err?.response?.status;

    if (status === 404) {
      await k8sRequest({
        url:     rawUrl('/api/v1/namespaces'),
        method:  'post',
        headers: { 'content-type': 'application/json' },
        data:    { apiVersion: 'v1', kind: 'Namespace', metadata: { name: OPERATOR_NAMESPACE } },
      });
    } else {
      throw err;
    }
  }
}

// duplicate check
async function hasActiveInstall(ns: string): Promise<boolean> {
  try {
    const list  = await k8sRequest({ url: crUrl() });
    const items: any[] = list?.items ?? [];

    return items.some((item: any) => {
      if (item.spec?.targetNamespace !== ns) return false;
      const conds: any[] = item.status?.conditions ?? [];
      const available   = conds.find((c: any) => c.type === 'Available');
      const progressing = conds.find((c: any) => c.type === 'Progressing');

      return available?.status === 'True' || progressing?.status === 'True';
    });
  } catch {
    return false;
  }
}

// submit
async function install() {
  errors.value = [];
  if (!domain.value.trim())          errors.value.push('Domain is required');
  if (!targetNamespace.value.trim()) errors.value.push('Target Namespace is required');
  if (!version.value.trim())         errors.value.push('Version is required');
  if (useExistingIngress.value && !nginxReleaseName.value.trim())      errors.value.push('Nginx Release Name is required when using an existing ingress');
  if (useExistingCertManager.value && !certManagerReleaseName.value.trim()) errors.value.push('Cert Manager Release Name is required when using an existing cert-manager');
  if (errors.value.length) return;

  busy.value = true;
  try {
    // Step A - bootstrap operator if not present
    await bootstrapOperator();

    // Step B - wait for CRD to be served (after fresh install)
    bootstrapStep.value = 'Verifying CRD availability…';
    await waitForCRDReady();

    // Step C - duplicate guard
    const duplicate = await hasActiveInstall(targetNamespace.value.trim());

    if (duplicate) {
      errors.value = [`An install for namespace "${ targetNamespace.value.trim() }" is already in progress or completed on this cluster.`];
      phase.value  = 'form';
      return;
    }

    // Step D — create InstallEpinio CR
    const res = await k8sRequest({
      url:     crUrl(),
      method:  'post',
      headers: { 'content-type': 'application/json' },
      data:    {
        apiVersion: `${ CR_GROUP }/${ CR_VERSION }`,
        kind:       'InstallEpinio',
        metadata:   { generateName: 'install-epinio-', namespace: OPERATOR_NAMESPACE },
        spec:       {
          domain:          domain.value.trim(),
          targetNamespace: targetNamespace.value.trim(),
          version:         version.value.trim(),
          ...(useExistingIngress.value && nginxReleaseName.value.trim() && {
            nginxReleaseName:       nginxReleaseName.value.trim(),
            ...(nginxReleaseNamespace.value.trim() && { nginxReleaseNamespace: nginxReleaseNamespace.value.trim() }),
          }),
          ...(useExistingCertManager.value && certManagerReleaseName.value.trim() && {
            certManagerReleaseName:       certManagerReleaseName.value.trim(),
            ...(certManagerReleaseNamespace.value.trim() && { certManagerReleaseNamespace: certManagerReleaseNamespace.value.trim() }),
          }),
        },
      },
    });

    crName.value        = res?.metadata?.name ?? '';
    statusMessage.value = 'Install request accepted — waiting for operator…';
    phase.value         = 'polling';
    pollCount           = 0;
    schedulePoll();
  } catch (err: any) {
    errors.value = [extractErrorMessage(err)];
    phase.value  = 'form';
  } finally {
    busy.value = false;
  }
}

// polling loop
function schedulePoll(delayMs = 4000) {
  pollCount++;
  if (pollCount > POLL_MAX) {
    statusMessage.value = 'Timed out waiting for the operator to start. The operator pod may have failed to pull its image or start. Check: kubectl get pods -n epinio-system';
    phase.value = 'failed';
    stopPolling();
    return;
  }
  pollTimer = setTimeout(poll, delayMs);
}

async function poll() {
  if (!crName.value) return;
  try {
    const cr         = await k8sRequest({ url: crUrl(crName.value) });
    const conditions: any[] = cr?.status?.conditions ?? [];

    const available   = conditions.find((c: any) => c.type === 'Available');
    const progressing = conditions.find((c: any) => c.type === 'Progressing');
    const degraded    = conditions.find((c: any) => c.type === 'Degraded');

    if (available?.status === 'True') {
      statusMessage.value = available.message || 'Epinio installed successfully.';
      phase.value = 'done';
      stopPolling();
      props.onSuccess?.();
    } else if (degraded?.status === 'True') {
      statusMessage.value = degraded.message || 'Install failed.';
      phase.value = 'failed';
      stopPolling();
    } else if (progressing?.status === 'True') {
      statusMessage.value = progressing.message || 'Install is in progress…';
      schedulePoll();
    } else {
      statusMessage.value = `Waiting for operator to start… (attempt ${ pollCount }/${ POLL_MAX })`;
      schedulePoll();
    }
  } catch (err: any) {
    statusMessage.value = `Could not reach the cluster (${ extractErrorMessage(err) }) — retrying… (${ pollCount }/${ POLL_MAX })`;
    schedulePoll(8000);
  }
}

function close() {
  stopPolling();
  emit('close');
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("h4", _hoisted_2, [
      _cache[10] || (_cache[10] = _createTextVNode("\n      Install Epinio on ", -1)),
      _createElementVNode("strong", null, _toDisplayString(_ctx.cluster.name), 1)
    ]),
    _cache[34] || (_cache[34] = _createTextVNode()),
    (phase.value === 'form')
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          (errors.value.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(errors.value, (err, idx) => {
                  return (_openBlock(), _createBlock(_unref(Banner), {
                    key: idx,
                    color: "error",
                    label: err
                  }, null, 8, ["label"]))
                }), 128))
              ]))
            : _createCommentVNode("", true),
          _cache[25] || (_cache[25] = _createTextVNode()),
          _createElementVNode("form", {
            class: "install-dialog__form",
            onSubmit: _cache[9] || (_cache[9] = _withModifiers(($event: any) => (!busy.value && install()), ["prevent"]))
          }, [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_unref(LabeledInput), {
                value: domain.value,
                "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((domain).value = $event)),
                label: "Domain",
                placeholder: "e.g. epinio.192.168.49.2.nip.io",
                required: true,
                disabled: busy.value
              }, null, 8, ["value", "disabled"])
            ]),
            _cache[18] || (_cache[18] = _createTextVNode()),
            _createElementVNode("div", _hoisted_5, [
              _createVNode(_unref(LabeledInput), {
                value: targetNamespace.value,
                "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((targetNamespace).value = $event)),
                label: "Target Namespace",
                placeholder: "epinio",
                required: true,
                disabled: busy.value
              }, null, 8, ["value", "disabled"])
            ]),
            _cache[19] || (_cache[19] = _createTextVNode()),
            _createElementVNode("div", _hoisted_6, [
              _createVNode(_unref(LabeledInput), {
                value: version.value,
                "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((version).value = $event)),
                label: "Version",
                placeholder: "e.g. 1.13.9",
                required: true,
                disabled: busy.value
              }, null, 8, ["value", "disabled"])
            ]),
            _cache[20] || (_cache[20] = _createTextVNode()),
            _createElementVNode("div", _hoisted_7, [
              _createElementVNode("label", _hoisted_8, [
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((useExistingIngress).value = $event)),
                  type: "checkbox",
                  disabled: busy.value
                }, null, 8, _hoisted_9), [
                  [_vModelCheckbox, useExistingIngress.value]
                ]),
                _cache[11] || (_cache[11] = _createTextVNode()),
                _cache[12] || (_cache[12] = _createElementVNode("span", null, "Use existing Nginx Ingress (skip installation)", -1))
              ])
            ]),
            _cache[21] || (_cache[21] = _createTextVNode()),
            (useExistingIngress.value)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createElementVNode("div", _hoisted_10, [
                    _createVNode(_unref(LabeledInput), {
                      value: nginxReleaseName.value,
                      "onUpdate:value": _cache[4] || (_cache[4] = ($event: any) => ((nginxReleaseName).value = $event)),
                      label: "Nginx Release Name",
                      placeholder: "e.g. rke2-ingress-nginx",
                      required: true,
                      disabled: busy.value
                    }, null, 8, ["value", "disabled"])
                  ]),
                  _cache[13] || (_cache[13] = _createTextVNode()),
                  _createElementVNode("div", _hoisted_11, [
                    _createVNode(_unref(LabeledInput), {
                      value: nginxReleaseNamespace.value,
                      "onUpdate:value": _cache[5] || (_cache[5] = ($event: any) => ((nginxReleaseNamespace).value = $event)),
                      label: "Nginx Release Namespace",
                      placeholder: "defaults to ingress-nginx",
                      disabled: busy.value
                    }, null, 8, ["value", "disabled"])
                  ])
                ], 64))
              : _createCommentVNode("", true),
            _cache[22] || (_cache[22] = _createTextVNode()),
            _createElementVNode("div", _hoisted_12, [
              _createElementVNode("label", _hoisted_13, [
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((useExistingCertManager).value = $event)),
                  type: "checkbox",
                  disabled: busy.value
                }, null, 8, _hoisted_14), [
                  [_vModelCheckbox, useExistingCertManager.value]
                ]),
                _cache[14] || (_cache[14] = _createTextVNode()),
                _cache[15] || (_cache[15] = _createElementVNode("span", null, "Use existing Cert Manager (skip installation)", -1))
              ])
            ]),
            _cache[23] || (_cache[23] = _createTextVNode()),
            (useExistingCertManager.value)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  _createElementVNode("div", _hoisted_15, [
                    _createVNode(_unref(LabeledInput), {
                      value: certManagerReleaseName.value,
                      "onUpdate:value": _cache[7] || (_cache[7] = ($event: any) => ((certManagerReleaseName).value = $event)),
                      label: "Cert Manager Release Name",
                      placeholder: "e.g. rancher-cert-manager",
                      required: true,
                      disabled: busy.value
                    }, null, 8, ["value", "disabled"])
                  ]),
                  _cache[16] || (_cache[16] = _createTextVNode()),
                  _createElementVNode("div", _hoisted_16, [
                    _createVNode(_unref(LabeledInput), {
                      value: certManagerReleaseNamespace.value,
                      "onUpdate:value": _cache[8] || (_cache[8] = ($event: any) => ((certManagerReleaseNamespace).value = $event)),
                      label: "Cert Manager Release Namespace",
                      placeholder: "defaults to cert-manager",
                      disabled: busy.value
                    }, null, 8, ["value", "disabled"])
                  ])
                ], 64))
              : _createCommentVNode("", true),
            _cache[24] || (_cache[24] = _createTextVNode()),
            _createElementVNode("div", _hoisted_17, [
              _createElementVNode("button", {
                type: "button",
                class: "btn role-secondary",
                disabled: busy.value,
                onClick: close
              }, "\n            Cancel\n          ", 8, _hoisted_18),
              _cache[17] || (_cache[17] = _createTextVNode()),
              _createElementVNode("button", {
                type: "submit",
                class: _normalizeClass(["btn role-primary", { disabled: busy.value }]),
                disabled: busy.value
              }, _toDisplayString(busy.value ? 'Working…' : 'Install'), 11, _hoisted_19)
            ])
          ], 32)
        ], 64))
      : (phase.value === 'bootstrapping')
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _createElementVNode("div", _hoisted_20, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(bootstrapSteps.value, (step) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: step.label,
                  class: _normalizeClass(["install-dialog__step", `install-dialog__step--${ step.status }`])
                }, [
                  _createElementVNode("span", _hoisted_21, [
                    (step.status === 'running')
                      ? (_openBlock(), _createElementBlock("i", _hoisted_22))
                      : (step.status === 'done')
                        ? (_openBlock(), _createElementBlock("i", _hoisted_23))
                        : (step.status === 'skipped')
                          ? (_openBlock(), _createElementBlock("i", _hoisted_24))
                          : (_openBlock(), _createElementBlock("i", _hoisted_25))
                  ]),
                  _cache[26] || (_cache[26] = _createTextVNode()),
                  _createElementVNode("span", null, _toDisplayString(step.label), 1),
                  _cache[27] || (_cache[27] = _createTextVNode()),
                  (step.status === 'running' && bootstrapStep.value)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_26, _toDisplayString(bootstrapStep.value), 1))
                    : _createCommentVNode("", true)
                ], 2))
              }), 128))
            ]),
            _cache[28] || (_cache[28] = _createTextVNode()),
            _createElementVNode("div", { class: "install-dialog__actions" }, [
              _createElementVNode("button", {
                class: "btn role-secondary",
                onClick: close
              }, "\n          Close (continues in background)\n        ")
            ])
          ], 64))
        : (phase.value === 'polling')
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
              _createElementVNode("div", _hoisted_27, [
                _cache[29] || (_cache[29] = _createElementVNode("i", { class: "icon icon-spinner icon-spin install-dialog__spinner" }, null, -1)),
                _cache[30] || (_cache[30] = _createTextVNode()),
                _createElementVNode("p", null, _toDisplayString(statusMessage.value), 1)
              ]),
              _cache[31] || (_cache[31] = _createTextVNode()),
              _createElementVNode("div", { class: "install-dialog__actions" }, [
                _createElementVNode("button", {
                  class: "btn role-secondary",
                  onClick: close
                }, "\n          Close (install continues in background)\n        ")
              ])
            ], 64))
          : (phase.value === 'done')
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                _createVNode(_unref(Banner), {
                  color: "success",
                  label: statusMessage.value
                }, null, 8, ["label"]),
                _cache[32] || (_cache[32] = _createTextVNode()),
                _createElementVNode("div", { class: "install-dialog__actions" }, [
                  _createElementVNode("button", {
                    class: "btn role-primary",
                    onClick: close
                  }, "\n          Close\n        ")
                ])
              ], 64))
            : (phase.value === 'failed')
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 4 }, [
                  _createVNode(_unref(Banner), {
                    color: "error",
                    label: statusMessage.value
                  }, null, 8, ["label"]),
                  _cache[33] || (_cache[33] = _createTextVNode()),
                  _createElementVNode("div", { class: "install-dialog__actions" }, [
                    _createElementVNode("button", {
                      class: "btn role-primary",
                      onClick: close
                    }, "\n          Close\n        ")
                  ])
                ], 64))
              : _createCommentVNode("", true)
  ]))
}
}

})